/**
 * SpeakEngine preset registry.
 *
 * Each preset is a self-contained recipe that tells the engine WHAT the user
 * is trying to capture and HOW to render the AI's structured output. Callers
 * either pass a preset id string (`preset: 'businessSetup'`) or an inline
 * preset object.
 *
 * Adding a new preset:
 *   1. Create the file (see notesAndFiles.js / businessSetup.js as templates).
 *   2. Export it here so `getPreset('id')` finds it.
 *   3. If it introduces a NEW schema.type value, update the backend
 *      structureVoiceMemoAny prompt-builder to handle it.
 */

import notesAndFiles from './notesAndFiles';
import businessSetup from './businessSetup';
import safeGuardRules from './safeGuardRules';
import imageLabPrompt from './imageLabPrompt';
import inboxReplyCase from './inboxReplyCase';

const PRESETS = {
  [notesAndFiles.id]: notesAndFiles,
  [businessSetup.id]: businessSetup,
  [safeGuardRules.id]: safeGuardRules,
  [imageLabPrompt.id]: imageLabPrompt,
  [inboxReplyCase.id]: inboxReplyCase,
};

export { notesAndFiles, businessSetup, safeGuardRules, imageLabPrompt, inboxReplyCase };

/**
 * Resolve a preset by id or by passing a full preset object through as-is.
 * Throws when an unknown id is supplied so misuse fails fast at call time
 * rather than showing an empty modal.
 */
export function getPreset(presetOrId) {
  if (!presetOrId) throw new Error('SpeakEngine: no preset supplied to openCapture()');
  if (typeof presetOrId === 'string') {
    const p = PRESETS[presetOrId];
    if (!p) throw new Error(`SpeakEngine: unknown preset id "${presetOrId}". Available: ${Object.keys(PRESETS).join(', ')}`);
    return p;
  }
  if (typeof presetOrId === 'object' && presetOrId.id && presetOrId.PreviewComponent) {
    return presetOrId;
  }
  throw new Error('SpeakEngine: preset must be a string id or an object with { id, PreviewComponent, schema }');
}

export default PRESETS;
